/*
 * Reporte de Movimientos (ficha 186): Asignaciones/Desasignaciones de plazas, Cambios de Sueldo y
 * Ausentismo. El usuario elige un rango de fechas de calendario (fecha del movimiento), tipos de
 * nómina opcionales y, en Ausentismo, los conceptos de incidencia. El backend devuelve una lista de
 * filas por hoja, con los datos tal como estaban al momento del movimiento, y aquí se arma el Excel
 * con una hoja por cada una.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Form, DatePicker, Button, Typography, message,
} from 'antd';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import Select from 'components/Select';
import { getReporteMovimientos } from 'api/nominas/reportesMovimientos';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import {
  FORMATO_FECHA,
  descargarWorkbook,
  escribirLineasEncabezado,
  escribirTabla,
} from 'utils/excelReportes';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const FORMATO_FECHA_API = 'YYYY-MM-DD';

// Columnas comunes a todas las hojas (formato del documento de requerimientos)
export const COLUMNAS_MOVIMIENTO = [
  { header: 'Empleado', key: 'empleado', width: 42 },
  { header: 'Puesto', key: 'puesto', width: 34 },
  { header: 'Plaza', key: 'plaza', width: 34 },
  { header: 'Área', key: 'area', width: 42 },
  { header: 'Tipo de Nómina Empleado', key: 'tipo_de_nomina_empleado', width: 24 },
  {
    header: 'Fecha de Movimiento', key: 'fecha_movimiento', width: 14, fecha: true,
  },
];

const ReporteMovimientosModal = ({
  open,
  onClose,
  tipo,
  titulo,
  hojas,
  conceptos,
  conceptosPorDefecto,
  conceptoLabel,
  nota,
  fileName,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tiposDeNomina, setTiposDeNomina] = useState([]);
  const entidad = useSelector((state) => state?.auth?.entidad);
  const conConceptos = !!conceptos;

  useEffect(() => {
    let mounted = true;
    if (open && !tiposDeNomina.length) {
      (async () => {
        const res = await getTiposDeNominasEmpleados();
        if (mounted) setTiposDeNomina(res?.results || res || []);
      })();
    }
    return () => { mounted = false; };
  }, [open]);

  const construirExcel = (data, valores) => {
    const workbook = new ExcelJS.Workbook();
    const tiposSeleccionados = tiposDeNomina
      .filter((t) => (valores.tipos_de_nomina_empleado || []).includes(t.id))
      .map((t) => t.descripcion);
    const conceptosSeleccionados = (conceptos || [])
      .filter((c) => (valores.conceptos || []).includes(c.id))
      .map((c) => c.label);

    hojas.forEach((hoja) => {
      const ws = workbook.addWorksheet(hoja.nombre);
      const encabezado = [
        'SISTEMA DE NÓMINAS',
        hoja.titulo,
        `Entidad: ${entidad?.nombre || ''}`,
        `Periodo: ${valores.rango[0].format(FORMATO_FECHA)} al ${valores.rango[1].format(FORMATO_FECHA)}`,
        `Tipos de Nómina: ${tiposSeleccionados.length ? tiposSeleccionados.join(', ') : 'Todos'}`,
        ...(conConceptos
          ? [`${conceptoLabel}: ${conceptosSeleccionados.length ? conceptosSeleccionados.join(', ') : 'Todos'}`]
          : []),
      ];
      // Hoja con armado propio (p. ej. Cronológico): recibe todas las listas del backend
      if (hoja.construir) {
        hoja.construir(ws, data.hojas || {}, encabezado);
        return;
      }
      const filas = data.hojas?.[hoja.key] || [];
      escribirLineasEncabezado(ws, [...encabezado, `Registros: ${filas.length}`]);
      escribirTabla(ws, [...COLUMNAS_MOVIMIENTO, ...hoja.columnas], filas);
    });
    return workbook;
  };

  const onGenerar = async () => {
    try {
      const valores = await form.validateFields();
      setLoading(true);
      const data = await getReporteMovimientos(tipo, {
        fecha_inicial: valores.rango[0].format(FORMATO_FECHA_API),
        fecha_final: valores.rango[1].format(FORMATO_FECHA_API),
        conceptos: valores.conceptos,
        tipos_de_nomina_empleado: valores.tipos_de_nomina_empleado,
      });
      if (!data) {
        setLoading(false);
        return;
      }
      const total = hojas.reduce((acc, h) => acc + (data.hojas?.[h.key]?.length || 0), 0);
      if (!total) {
        message.info('No se encontraron movimientos en el periodo con los filtros seleccionados.');
      } else {
        const workbook = construirExcel(data, valores);
        const desde = valores.rango[0].format('DD-MM-YYYY');
        const hasta = valores.rango[1].format('DD-MM-YYYY');
        await descargarWorkbook(workbook, `${fileName}_${desde}_al_${hasta}.xlsx`);
      }
      setLoading(false);
    } catch (err) {
      // Errores de validación del formulario: antd ya los muestra en el campo
      setLoading(false);
    }
  };

  return (
    <Modal
      title={titulo}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancelar" onClick={onClose}>Cancelar</Button>,
        <Button key="generar" type="primary" loading={loading} onClick={onGenerar}>
          Generar Excel
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ conceptos: conceptosPorDefecto }}
      >
        <Form.Item
          name="rango"
          label="Periodo"
          rules={[{ required: true, message: 'El campo es requerido' }]}
          extra="Fecha en que ocurrió el movimiento."
        >
          <RangePicker format={FORMATO_FECHA} style={{ width: '100%' }} />
        </Form.Item>
        {conConceptos && (
          <Form.Item name="conceptos" label={conceptoLabel} extra="Vacío = todos los de tipo Ausentismo e Incapacidad.">
            <Select
              mode="multiple"
              dataSource={conceptos}
              keyProp="id"
              render={(item) => item.label}
              allowClear
            />
          </Form.Item>
        )}
        <Form.Item name="tipos_de_nomina_empleado" label="Tipos de Nómina" extra="Vacío = todos.">
          <Select
            mode="multiple"
            dataSource={tiposDeNomina}
            render={(item) => item.descripcion}
            allowClear
          />
        </Form.Item>
        {nota && <Text type="secondary">{nota}</Text>}
      </Form>
    </Modal>
  );
};

ReporteMovimientosModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tipo: PropTypes.oneOf(['plazas', 'sueldos', 'ausentismo']).isRequired,
  titulo: PropTypes.string.isRequired,
  // Una hoja por cada lista que devuelve el backend en `hojas[key]`. `columnas` va después de
  // las comunes (COLUMNAS_MOVIMIENTO): { header, key, width, numFmt?, fecha? }.
  // Alternativa: `construir(ws, hojasData, lineasEncabezado)` arma la hoja por su cuenta.
  hojas: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    nombre: PropTypes.string.isRequired,
    titulo: PropTypes.string.isRequired,
    columnas: PropTypes.arrayOf(PropTypes.shape({
      header: PropTypes.string,
      key: PropTypes.string,
      width: PropTypes.number,
      numFmt: PropTypes.string,
      fecha: PropTypes.bool,
    })),
    construir: PropTypes.func,
  })).isRequired,
  // Solo Ausentismo: [{ id, label }]; si se omite no se muestra el selector
  conceptos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
  })),
  conceptosPorDefecto: PropTypes.arrayOf(PropTypes.number),
  conceptoLabel: PropTypes.string,
  nota: PropTypes.string,
  fileName: PropTypes.string.isRequired,
};

ReporteMovimientosModal.defaultProps = {
  conceptos: null,
  conceptosPorDefecto: [],
  conceptoLabel: 'Conceptos',
  nota: '',
};

export default ReporteMovimientosModal;
